import { PDFDownloadLink } from "@react-pdf/renderer";
import axios from "axios";
import dayjs from "dayjs";
import { jsPDF } from "jspdf"; // Import jsPDF library
import React, { useEffect, useState } from "react";
import MyPDFDocument from "../Components/pdfcomponent/PDFDocument";
import { baseurl } from "../config/url";
import { showErrorToast } from "../utils/toaster";

const formattedDate = dayjs().format("DD MMMM YYYY");

const UserProductInvoice = ({ products, ammount, shippingCost, saleMode }) => {
  const [sellerDetailsMap, setSellerDetailsMap] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [loadingSellers, setLoadingSellers] = useState(true);
  const [loadingUserDetails, setLoadingUserDetails] = useState(true);
  const [errorSellers, setErrorSellers] = useState(null);
  const [errorUserDetails, setErrorUserDetails] = useState(null);

  // Fetch Seller Details
  useEffect(() => {
    const fetchAllSellerDetails = async () => {
      try {
        if (!products || products.length === 0) {
          console.error("Product details are empty.");
          // showErrorToast("Product details are not available.");
          setLoadingSellers(false);
          return;
        }

        const uniqueSellerIds = [
          ...new Set(products.map((product) => product.seller)),
        ];

        const sellerDetails = {};
        await Promise.all(
          uniqueSellerIds.map(async (sellerId) => {
            const response = await axios.get(
              `${baseurl}/api/seller/seller-by-id/${sellerId}`
            );
            sellerDetails[sellerId] = response.data;
          })
        );

        setSellerDetailsMap(sellerDetails);
        setLoadingSellers(false);
      } catch (err) {
        console.error("Error fetching seller details:", err);
        setErrorSellers("Failed to load seller details.");
        setLoadingSellers(false);
      }
    };

    fetchAllSellerDetails();
  }, [products]);

  // Fetch User Details
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userIdFromLocalStorage = localStorage.getItem("userId");
        if (!userIdFromLocalStorage) {
          console.error("User ID is not available in local storage.");
          showErrorToast("User ID is not available in local storage");
          setLoadingUserDetails(false);
          return;
        }

        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${baseurl}/api/user/users-by-id/${userIdFromLocalStorage}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setUserDetails(response.data);
        setLoadingUserDetails(false);
      } catch (err) {
        console.error("Error fetching user details:", err);
        setErrorUserDetails("Failed to load user details.");
        setLoadingUserDetails(false);
      }
    };

    fetchUserDetails();
  }, []);

  // Compute Summary Data
  const calculateSummary = () => {
    let itemsTotal = 0;
    let totalTax = 0;

    if (products && products.length > 0) {
      products.forEach((product) => {
        const itemTotal = product.productPrice * product.quantity;
        const tax = (itemTotal * product.gstNumber) / 100;
        itemsTotal += itemTotal;
        totalTax += tax;
      });
    }

    const total = itemsTotal + totalTax + shippingCost; // Including additional charges (e.g., shipping)

    return {
      itemsTotal: itemsTotal.toFixed(2),
      totalTax: totalTax.toFixed(2),
      total: total.toFixed(2),
    };
  };

  const summary = calculateSummary();

  // Generate PDF
  const generatePDF = () => {
    const doc = new jsPDF();

    // Set title and formatted date
    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.text("Invoice", 14, 20);
    doc.setFontSize(10);
    doc.text(`Date: ${formattedDate}`, 14, 30);

    // Add Sale Mode
    doc.autoTable({
      startY: 40,
      head: [["Sale Mode", "Items Total", "Total Tax", "Shipping", "Total"]],
      body: [
        [
          saleMode || "N/A",
          `₹${summary.itemsTotal}`,
          `₹${summary.totalTax}`,
          `₹${shippingCost}`,
          `₹${summary.total}`,
        ],
      ],
    });

    // User Details Section
    if (loadingUserDetails) {
      doc.text("Loading user details...", 14, doc.lastAutoTable.finalY + 10);
    } else if (userDetails) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Field", "Details"]],
        body: [
          ["Name", userDetails.name || "N/A"],
          ["Email", userDetails.email || "N/A"],
          ["Contact", userDetails.mobileNumber || "N/A"],
          [
            "Address",
            `${userDetails.address?.street || "N/A"}, ${userDetails.address?.city || "N/A"}, ${userDetails.address?.state || "N/A"}, ${userDetails.address?.country || "N/A"} - ${userDetails.address?.postalCode || "N/A"}`,
          ],
        ],
        styles: { fontSize: 10 },
      });
    } else {
      doc.text("No user details available.", 14, doc.lastAutoTable.finalY + 10);
    }

    // Product and Seller Details Section
doc.text("Products and Seller Details", 14, doc.lastAutoTable.finalY + 10);

if (loadingSellers) {
  doc.text("Loading seller details...", 14, doc.lastAutoTable.finalY + 20);
} else if (errorSellers) {
  doc.setTextColor(255, 0, 0);
  doc.text(errorSellers, 14, doc.lastAutoTable.finalY + 20);
  doc.setTextColor(0, 0, 0);
} else {
  products.forEach((product, index) => {
    const sellerDetails = sellerDetailsMap[product.seller];

    // Product Details Table
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [["Field", "Details"]],
      body: [
        ["Product Name", product.productName || "N/A"],
        ["Price", `₹${product.productPrice}`],
        ["Quantity", product.quantity || "N/A"],
        ["HSN Code", product.hsnCode || "N/A"],
        ["GST Percentage", `${product.gstNumber}%` || "N/A"],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
      columnStyles: {
        0: { halign: "left", cellWidth: 50 },
        1: { halign: "right", cellWidth: 90 },
      },
    });

    // Seller Details Table (if available)
    if (sellerDetails) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 5,
        head: [["Field", "Details"]],
        body: [
          ["Name", sellerDetails.name || "N/A"],
          ["Email", sellerDetails.email || "N/A"],
          ["Contact", sellerDetails.mobile || "N/A"],
          [
            "Address",
            `${sellerDetails.address?.street || "N/A"}, ${sellerDetails.address?.city || "N/A"}, ${sellerDetails.address?.state || "N/A"}, ${sellerDetails.address?.country || "N/A"} - ${sellerDetails.address?.postalCode || "N/A"}`,
          ],
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
        columnStyles: {
          0: { halign: "left", cellWidth: 50 },
          1: { halign: "right", cellWidth: 90 },
        },
      });
    } else {
      doc.text("Seller details not available.", 14, doc.lastAutoTable.finalY + 10);
    }
  });
}

    // Save the generated PDF
    doc.save("invoice.pdf");
  };

  return (
    <div className="invoice-section p-4 border rounded-md shadow-md">
      {/* Header Section */}
      <div>
        <div className="text-2xl font-bold text-gray-800">
          <h1>Performa Invoice</h1>
          <h2>Order Summary</h2>
        </div>
        <p className="text-gray-600">Date: {formattedDate}</p>
      </div>
  
      {/* Sale Details */}
      <div className="mt-6">
        <table className="w-full text-left border-collapse border border-gray-300">
          <tbody>
            <tr>
              <td className="border px-4 py-2 font-semibold">Sale Mode</td>
              <td className="border px-4 py-2">{saleMode}</td>
            </tr>
            <tr>
              <td className="border px-4 py-2 font-semibold">Items Total</td>
              <td className="border px-4 py-2">₹{summary.itemsTotal}</td>
            </tr>
            <tr>
              <td className="border px-4 py-2 font-semibold">Total Tax</td>
              <td className="border px-4 py-2">₹{summary.totalTax}</td>
            </tr>
            <tr>
              <td className="border px-4 py-2 font-semibold">Shipping</td>
              <td className="border px-4 py-2">₹{shippingCost}</td>
            </tr>
            <tr>
              <td className="border px-4 py-2 font-semibold">Total</td>
              <td className="border px-4 py-2">₹{summary.total}</td>
            </tr>
          </tbody>
        </table>
      </div>
  
      {/* User Details */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">User Details</h2>
        {loadingUserDetails ? (
          <p className="text-gray-500">Loading user details...</p>
        ) : errorUserDetails ? (
          <p className="text-red-500">{errorUserDetails}</p>
        ) : userDetails ? (
          <table className="w-full text-left border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border px-4 py-2 font-semibold">Name</td>
                <td className="border px-4 py-2">{userDetails.name}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2 font-semibold">Email</td>
                <td className="border px-4 py-2">{userDetails.email}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2 font-semibold">Contact</td>
                <td className="border px-4 py-2">{userDetails.mobileNumber}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2 font-semibold">Address</td>
                <td className="border px-4 py-2">
                  {`${userDetails.address.street}, ${userDetails.address.city}, ${userDetails.address.state}, ${userDetails.address.country} - ${userDetails.address.postalCode}`}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">No user details available.</p>
        )}
      </div>
  
      {/* Products and Seller Details */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Products and Seller Details</h2>
        {loadingSellers ? (
          <p className="text-gray-500">Loading seller details...</p>
        ) : errorSellers ? (
          <p className="text-red-500">{errorSellers}</p>
        ) : (
          products.map((product, index) => {
            const sellerDetails = sellerDetailsMap[product.seller];
            return (
              <div key={index} className="mb-4 border-b pb-4">
                <h3 className="text-md font-bold text-gray-700 mb-2">Product {index + 1}</h3>
                <table className="w-full text-left border-collapse border border-gray-300">
                  <tbody>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">Product Name</td>
                      <td className="border px-4 py-2">{product.productName}</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">Price</td>
                      <td className="border px-4 py-2">₹{product.productPrice}</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">Quantity</td>
                      <td className="border px-4 py-2">{product.quantity}</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">HSN Code</td>
                      <td className="border px-4 py-2">{product.hsnCode}</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">GST Percentage</td>
                      <td className="border px-4 py-2">{product.gstNumber}%</td>
                    </tr>
                  </tbody>
                </table>
  
                {sellerDetails ? (
                  <table className="w-full mt-2 text-left border-collapse border border-gray-300">
                    <thead>
                      <tr>
                        <th colSpan="2" className="px-4 py-2 text-left text-md font-bold text-gray-700">
                          Seller Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border px-4 py-2 font-semibold">Name</td>
                        <td className="border px-4 py-2">{sellerDetails.name}</td>
                      </tr>
                      <tr>
                        <td className="border px-4 py-2 font-semibold">Email</td>
                        <td className="border px-4 py-2">{sellerDetails.email}</td>
                      </tr>
                      <tr>
                        <td className="border px-4 py-2 font-semibold">Contact</td>
                        <td className="border px-4 py-2">{sellerDetails.mobile}</td>
                      </tr>
                      <tr>
                        <td className="border px-4 py-2 font-semibold">Address</td>
                        <td className="border px-4 py-2">
                          {`${sellerDetails.address.street}, ${sellerDetails.address.city}, ${sellerDetails.address.state}, ${sellerDetails.address.country} - ${sellerDetails.address.postalCode}`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-500">Seller details not available.</p>
                )}
              </div>
            );
          })
        )}
      </div>
    

      {/* Download Button */}
      <div className="mt-4">
        <button
          className="bg-blue-500 text-white py-2 px-6 rounded-md"
          onClick={generatePDF}
        >
          Download Invoice (PDF)
        </button>
      </div>

      {/* pdf code */}
      <div style={{ textAlign: "center", padding: 20 }}>
        {/* Download PDF button */}
        <br />
        {/* <PDFDownloadLink */}
          {/* document={<MyPDFDocument />} */}
          {/* fileName="my_document.pdf" */}
        {/* > */}
          {/* {({ loading }) => */}
            {/* loading ? ( */}
              {/* <button>Loading PDF...</button> */}
            {/* ) : ( */}
              {/* <button>Download PDF Test</button> */}
            {/* ) */}
          {/* } */}
        {/* </PDFDownloadLink> */}
      </div>
    </div>
  );
};

export default UserProductInvoice;